import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messageCacheService } from '../../services/message-cache.service';
import { AdminConversation } from '../../services/message-admin.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

function timeAgo(ts: number | null): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Ahora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(ts).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

export function MessagesPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<AdminConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    messageCacheService
      .loadConversations()
      .then(setConversations)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Mensajes</h1>
        <p className="text-xs text-slate-500">
          {conversations.length} conversación{conversations.length === 1 ? '' : 'es'}
        </p>
      </div>

      {loading && conversations.length === 0 ? (
        <LoadingState />
      ) : error && conversations.length === 0 ? (
        <ErrorState message={error} onRetry={load} />
      ) : conversations.length === 0 ? (
        <EmptyState
          icon="💬"
          title="Sin conversaciones"
          description="No lideras proyectos con mensajes aún."
        />
      ) : (
        <ul className="space-y-2">
          {conversations.map((c) => (
            <li key={c.project_id}>
              <button
                onClick={() => navigate(`/gestion/mensajes/${c.project_id}`)}
                className="w-full text-left bg-white rounded-xl shadow-sm p-4 active:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {c.client_name}
                  </p>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">
                    {timeAgo(c.last_message_at)}
                  </span>
                </div>
                <p className="text-xs text-primary truncate">{c.project_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-slate-500 truncate flex-1">
                    {c.last_message_preview ?? 'Sin mensajes'}
                  </p>
                  {c.unread_count > 0 && (
                    <span className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center flex-shrink-0">
                      {c.unread_count > 9 ? '9+' : c.unread_count}
                    </span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}